/**
 * SaveRoadmapItemLikeUseCase
 *
 * Ports Flutter's roadmap like/dislike toggle algorithm
 * (`RoadmapScreenState._likeTapped` / `_dislikeTapped`,
 * lib/presentation/screens/roadmap/roadmap_screen.dart:189-351) into a single
 * use case parameterised on the `liked` boolean:
 *   - `liked === true`  → the like branch (`_likeTapped`, lines 211-270)
 *   - `liked === false` → the dislike branch (`_dislikeTapped`, the exact
 *     mirror, lines 295-350: `liked`↔`disliked`, `likes`↔`dislikes`,
 *     `likesIncrement`↔`dislikesIncrement`).
 *
 * The toggle math is identical to Flutter:
 *   1. Read the current local like-state from SecureStorage
 *      (`_getLocalRoadmapItemLikeEntity`, lines 396-400 — keyed on
 *      `SECURE_STORAGE_KEYS.ROADMAP_LIKES + item.id`).
 *   2. First-ever reaction → set the reaction `true` and `+1`.
 *      Repeat tap → toggle it off (`-1`, `isUndo = true`).
 *      Switching from the opposite reaction → also decrement the opposite count.
 *   3. Optimistically write the new like-state, call the `saveRoadmapItemLike`
 *      Cloud Function, and revert the storage write on `!success` (restore the
 *      previous record, or delete the key when there was none) — mirroring
 *      `likeCommentUseCase`/`toggleUserMediaLikeUseCase`.
 *
 * Architecture differences from Flutter (allowed):
 *   - The new stored record always clears the opposite reaction (`disliked =
 *     false` on a like, `liked = false` on a dislike). Flutter writes a fresh
 *     lean Hive entity whose opposite flag defaults to `false`; web matches.
 *   - This use case does NOT mutate `item` in place. Flutter mutates
 *     `item.likes` / `item.dislikes`; web keeps the use case pure and returns
 *     the `likesDelta` / `dislikesDelta` (the `likesIncrement` /
 *     `dislikesIncrement`, including any cross-clear decrement) so the hook can
 *     apply them eager-optimistically to the in-memory item's **counts** before
 *     the Cloud Function resolves and revert on failure — matching Flutter's
 *     mutate-before-await and the canonical web `toggleUserMediaLikeUseCase`.
 *   - This is an `execute`-only use case (mirroring `toggleUserMediaLikeUseCase`):
 *     a single call that (A) writes the optimistic local-storage record,
 *     (B) calls the server, and (C) reverts the storage write on failure. The
 *     reactive **icon-tint** re-render does NOT flow back through this use case's
 *     return value — it flows from the optimistic storage write (A) firing a
 *     `ROADMAP_LIKES`-prefix storage listener in the hook, the web equivalent of
 *     Flutter's Hive box watch (`_onRoadmapItemLikeChange`,
 *     roadmap_screen.dart:380-394). `likeEntity` is still returned (it does no
 *     harm and documents the new state), but the tint source of truth is the
 *     listener, not the caller threading this value into React state.
 *   - `HapticFeedback.lightImpact()` (line 246-248) has no web equivalent and
 *     is intentionally not ported.
 *   - The web `RoadmapItemLikeStorage` shape is richer than Flutter's lean Hive
 *     entity (it carries `userId`/`timestamp`/`userName`); these are populated
 *     so a later sync/read round-trips.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { roadmapItemLikesService, RoadmapItemLikesService } from '@/domain/services/roadmap/roadmapItemLikesService';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import type { RoadmapItemLikeEntity } from '@/domain/entities/roadmap/roadmapItemLikeEntity';
import type { RoadmapItemLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { RoadmapItemLikeRequestDto } from '@/data/dtos/roadmapItemLike/roadmapItemLikeRequestDto';

export interface SaveRoadmapItemLikeParams {
  item: RoadmapItemEntity;
  /**
   * `true` runs the like branch (`_likeTapped`); `false` runs the dislike
   * (mirror) branch (`_dislikeTapped`). A boolean, not a `'like' | 'dislike'`
   * union — matches the read side (`getRoadmapItemLikes(…, liked: boolean)`)
   * and `fetchContentLikedUsersUseCase`.
   */
  liked: boolean;
  /** Current user's name — `currentUser.name` in Flutter (request.username). */
  username: string;
  /**
   * Current user's Firebase id — `currentUser.firebaseId` in Flutter. Stored on
   * the richer web `RoadmapItemLikeStorage.userId`. (Flutter writes only the
   * lean Hive entity, so it has no equivalent field; see header note.)
   */
  currentUserId: string;
}

export interface SaveRoadmapItemLikeResult {
  /** Whether the `saveRoadmapItemLike` Cloud Function returned `success`. */
  success: boolean;
  /**
   * The new local like-state (the lean domain entity). The icon tint flows via
   * the hook's `ROADMAP_LIKES` storage listener (fired by the optimistic write),
   * not this field — but it is returned because it does no harm and documents
   * the post-toggle state.
   */
  likeEntity: RoadmapItemLikeEntity;
  /**
   * The change to apply to `item.likes` (the request's `likesIncrement`,
   * including any cross-clear decrement). The hook applies this
   * eager-optimistically to the in-memory count and reverts on failure; the use
   * case does not mutate `item`.
   */
  likesDelta: number;
  /** The change to apply to `item.dislikes` (the request's `dislikesIncrement`). */
  dislikesDelta: number;
}

/**
 * Internal: the computed toggle outcome (the new like-state + the request the
 * Cloud Function receives). Keeps `execute` thin and the branch math testable.
 */
interface ToggleComputation {
  likeEntity: RoadmapItemLikeEntity;
  request: RoadmapItemLikeRequestDto;
}

export class SaveRoadmapItemLikeUseCase {
  private storage: SecureStorageWebCrypto;
  private likesService: RoadmapItemLikesService;

  constructor(
    storage?: SecureStorageWebCrypto,
    likesService?: RoadmapItemLikesService,
  ) {
    this.storage = storage ?? SecureStorageWebCrypto.getInstance();
    this.likesService = likesService ?? roadmapItemLikesService;
  }

  /**
   * Run the whole toggle in one call (mirroring `toggleUserMediaLikeUseCase`):
   *   1. read the previous local like-state from SecureStorage;
   *   2. compute the toggle (`computeToggle`);
   *   (A) optimistically write the new like-state record — this storage write is
   *       what fires the hook's `ROADMAP_LIKES`-prefix listener to re-tint the
   *       icon (the web port of Flutter's Hive box watch);
   *   (B) call the server via the likes service (Finding 3's routing:
   *       service → cloudFunctionsService.saveRoadmapItemLike);
   *   (C) on `!success`, revert the optimistic storage write (restore the
   *       previous record, or delete the key when there was none).
   *
   * Returns the CF `success` plus the count deltas (`likesDelta`/`dislikesDelta`)
   * the hook applies eager-optimistically to the in-memory counts and reverts on
   * failure — the counts live on the in-memory item, not in storage, so they
   * cannot flow through the listener (same as `toggleUserMediaLikeUseCase`).
   */
  async execute(params: SaveRoadmapItemLikeParams): Promise<SaveRoadmapItemLikeResult> {
    const { item, liked, username, currentUserId } = params;

    const storageKey = SECURE_STORAGE_KEYS.ROADMAP_LIKES + item.id;
    const previous = await this.storage.getEncrypted<RoadmapItemLikeStorage>(storageKey);

    const { likeEntity, request } = this.computeToggle(item, liked, username, previous);

    // (A) Optimistically write the new like-state under the same key (the richer
    // web storage shape; Flutter writes only the lean Hive entity). This write
    // fires the hook's storage-prefix listener, which re-tints the matching item.
    const newRecord: RoadmapItemLikeStorage = {
      roadmapItemId: likeEntity.roadmapItemId,
      userId: currentUserId,
      liked: likeEntity.liked,
      disliked: likeEntity.disliked,
      timestamp: Date.now(),
      userName: username,
    };
    await this.storage.setEncrypted(storageKey, newRecord);

    // (B) Call the server (Finding 3 routing: service → cloudFunctionsService).
    const success = await this.likesService.saveRoadmapItemLike(request);

    if (!success) {
      // (C) Revert local storage (mirror likeCommentUseCase.ts:51-57): restore
      // the previous record when it existed, else delete the key.
      if (previous) {
        await this.storage.setEncrypted(storageKey, previous);
      } else {
        await this.storage.remove(storageKey);
      }
    }

    return {
      success,
      likeEntity,
      likesDelta: request.likesIncrement,
      dislikesDelta: request.dislikesIncrement,
    };
  }

  /**
   * The toggle math from `_likeTapped` (roadmap_screen.dart:211-244) and its
   * mirror `_dislikeTapped` (:295-328), parameterised on `liked`. Returns the
   * new lean like-state plus the fully-built request the Cloud Function gets.
   *
   * `liked === true`: the "primary" reaction is `liked`, the "opposite" is
   * `disliked` (cross-clear decrements `dislikesIncrement`). `liked === false`
   * swaps them.
   */
  private computeToggle(
    item: RoadmapItemEntity,
    liked: boolean,
    username: string,
    previous: RoadmapItemLikeStorage | null,
  ): ToggleComputation {
    // The new lean like-state. The opposite reaction is left at its default
    // `false`, so writing this record implicitly clears the opposite reaction
    // (Flutter relies on the same default — it builds a fresh entity).
    const likeEntity: RoadmapItemLikeEntity = {
      roadmapItemId: item.id,
      liked: false,
      disliked: false,
    };

    // Base request mirrors `RoadmapItemLikeRequest`'s defaults (Flutter
    // roadmap_item_like_request.dart:16-21): increments/flags 0/false.
    const request: RoadmapItemLikeRequestDto = {
      roadmapItemId: item.id,
      ownerUserId: item.userId,
      likesIncrement: 0,
      dislikesIncrement: 0,
      liked: false,
      disliked: false,
      isUndo: false,
      username,
    };

    if (previous == null) {
      // First reaction (roadmap_screen.dart:222-226 like / :306-310 dislike):
      // set the reaction `true`, `+1`.
      if (liked) {
        likeEntity.liked = true;
        request.liked = true;
        request.likesIncrement = 1;
      } else {
        likeEntity.disliked = true;
        request.disliked = true;
        request.dislikesIncrement = 1;
      }
      return { likeEntity, request };
    }

    // A record exists (roadmap_screen.dart:227-243 like / :311-327 dislike).
    const previouslyReacted = liked ? previous.liked : previous.disliked;
    const newReaction = !previouslyReacted;

    if (liked) {
      likeEntity.liked = newReaction;
      request.liked = newReaction;
      if (previouslyReacted) {
        request.likesIncrement = -1;
        request.isUndo = true;
      } else {
        request.likesIncrement = 1;
      }
      // Cross-clear: the user had disliked → decrement the opposite count.
      if (previous.disliked) {
        request.dislikesIncrement = -1;
      }
    } else {
      likeEntity.disliked = newReaction;
      request.disliked = newReaction;
      if (previouslyReacted) {
        request.dislikesIncrement = -1;
        request.isUndo = true;
      } else {
        request.dislikesIncrement = 1;
      }
      // Cross-clear: the user had liked → decrement the opposite count.
      if (previous.liked) {
        request.likesIncrement = -1;
      }
    }

    return { likeEntity, request };
  }
}

export const saveRoadmapItemLikeUseCase = new SaveRoadmapItemLikeUseCase();
